import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { IOSView, isIOS } from "react-device-detect";
import DownloadIcon from "@mui/icons-material/Download";
import TitleTooltip from "../Tooltip/TitleTooltip";
import CircularProgressWithLabel from "../loader/CircularProgressWithLabel";
import VideoPlayer from "./VideoPlayer";
import WebmPlayer from "../JWPlayer/WebmPlayer";

const VideoJS = (props) => {
  const [fileURL, setFileUrl] = useState(props.fileURL);
  const [autoplay, setAutoplay] = useState(props.isAutoplay ?? true);
  const [slowMo, setSlowMo] = useState(props.isSlowMo ?? false);
  const [download, setDownload] = useState(props.isDownload ?? false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [extension, setExtension] = useState("");

  useEffect(() => {
    setFileUrl(props.fileURL);
    let extension = props.fileName
      .substring(props.fileName.lastIndexOf(".") + 1)
      .toLowerCase();
    setExtension(extension);
  }, [props.fileURL]);

  const isMacOS = () => {
    return navigator.userAgent.indexOf("Macintosh") !== -1;
  };

  const videoJsOptions = {
    autoplay: autoplay,
    controls: true,
    paused: true,
    playerd: true,
    controlBar: {
      playToggle: false,
      captionsButton: false,
      chaptersButton: false,
      subtitlesButton: false,
      remainingTimeDisplay: false,
      fullscreenToggle: true,
      playbackRateMenuButton: slowMo,
    },
    responsive: false,
    fill: true,
    fluid: true,
    poster: props.image,
    experimentalSvgIcons: true,
    playbackRates: [0.25, 0.5, 0.75, 1],
    sources: [
      {
        src: fileURL,
        // type: `video/${extension}`,
      },
    ],
  };

  //... Utility to download video with progress tracking...
  const downloadVideo = async () => {
    setIsDownloading(true);
    setDownloadProgress(5);

    try {
      const response = await fetch(fileURL);
      const contentLength = response.headers.get("Content-Length");

      if (!response.ok) {
        throw new Error(
          `Failed to fetch data: ${response.status} ${response.statusText}`
        );
      }

      const fileSize = parseInt(contentLength, 10);

      const SMALL_FILE_THRESHOLD = 10 * 1024 * 1024;
      if (fileSize < SMALL_FILE_THRESHOLD) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      //... Start downloading the file
      const reader = response.body.getReader();
      const total = fileSize;
      let loaded = 0;
      const chunks = [];

      //... Read the data in chunks and calculate progress
      const pump = async () => {
        const { done, value } = await reader.read();
        if (done) {
          setIsDownloading(false);
          const blob = new Blob(chunks);
          const videoURLBlob = URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = videoURLBlob;
          a.download = `${props.fileName}`;
          document.body.append(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(videoURLBlob);
          return;
        }

        chunks.push(value);
        loaded += value.length;

        let progress = Math.round(((loaded + fileSize * 0.05) / total) * 100);
        progress = Math.min(progress, 100);
        setDownloadProgress(progress);

        pump();
      };

      pump();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div style={{ display: "flex", position: "relative" }}>
      {(isIOS || isMacOS()) && extension === "webm" ? (
        <>
          {/* <SharePointVideoPlayer
              fileName={props.fileName}
            /> */}
          <WebmPlayer src={fileURL} />
        </>
      ) : (
        <VideoPlayer className={"vjs-player"} options={videoJsOptions} />
      )}

      {download && (
        <div style={{ position: "absolute", right: "1rem", top: "1rem" }}>
          {!isDownloading ? (
            <TitleTooltip title="Download Video">
              <IconButton onClick={downloadVideo}>
                <DownloadIcon
                  sx={{
                    color: "white",
                    fontSize: {
                      xs: "20px",
                      sm: "20px",
                      md: "25px",
                      lg: "25px",
                      xl: "25px",
                    },
                  }}
                />
              </IconButton>
            </TitleTooltip>
          ) : (
            <Box sx={{ marginTop: "8px" }}>
              <CircularProgressWithLabel value={downloadProgress} />
            </Box>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(VideoJS);
